/**
 * CR-7058: Navigation Menu Management is an access control, not a cosmetic
 * toggle.
 *
 * Switching a page off in Platform Admin → Platform → Navigation Menu
 * Management used to do one thing: drop the item from the sidebar. The page
 * itself stayed reachable by typing the URL, following a bookmark or a link
 * from a notification / global search. These helpers are the single source of
 * truth for "may this user open this navigation entry", shared by the Sidebar
 * (to hide the item) and by NavigationAccessGate (to block the route), so the
 * two can never disagree.
 *
 * Only the module-style rules live here — the tenant's enabled-module
 * allowlist, the admin menu toggle and the user's module permission. Feature
 * flags and admin-only surfaces are separate concerns and stay in the Sidebar.
 */
import type { NavItem } from '../../config/navigation';
import type {
  MenuVisibility,
  PurchasingTabsVisibility,
  QualityTabsVisibility,
} from '../../lib/api/types/common';

export type ModuleKey = 'purchasing' | 'quality' | 'finance' | 'compliance';

export interface NavAccessContext {
  /** True when the supplier-side sidebar is rendered (drives supplierVisibilityKey). */
  isSupplierSidebar: boolean;
  /** Per-company module allowlist from ViewContext, or null when unrestricted. */
  enabledModules: string[] | null;
  /** Admin toggle state from MenuSettingsContext. */
  isMenuItemVisible: (key: keyof MenuVisibility) => boolean;
  /** User-level module permission from UserPermissionsContext. */
  hasModuleAccess: (module: ModuleKey) => boolean;
  /** Whether this user may open Admin Setup regardless of the toggle. */
  canOpenAdminSetup: boolean;
}

/**
 * Map a menu-visibility key to the user-permission module it belongs to.
 *
 * @param key - the nav item's visibilityKey
 * @returns the module whose user permission also gates the item, or null
 */
export function getModuleForVisibilityKey(key: keyof MenuVisibility): ModuleKey | null {
  switch (key) {
    case 'purchasing':
    case 'purchaseOrders':
      return 'purchasing';
    case 'quality':
      return 'quality';
    // CR-8252: Supplier Onboarding is the compliance module (its CR-8672 switch),
    // and so is the Questionnaires section on both sides (Aaron, 27 Oct 2026).
    case 'onboarding':
    case 'questionnaire':
    case 'supplierQuestionnaires':
      return 'compliance';
    // Finance module can be added here when needed
    default:
      return null;
  }
}

/**
 * Decide whether a navigation entry is enabled for this user by the
 * module-style configuration (tenant allowlist, admin toggle, user module
 * permission). Items without a visibilityKey are always enabled.
 *
 * @param item - the nav item to check
 * @param ctx - the resolved access context
 * @returns true when the entry (and its page) may be opened
 */
export function isNavItemEnabledByConfig(item: NavItem, ctx: NavAccessContext): boolean {
  if (!item.visibilityKey) return true;

  // Per-company module restriction — if enabledModules is set, only listed
  // modules are available. Keyed on the original visibilityKey (purchasing /
  // quality / dashboard / …), not on per-role overrides.
  if (ctx.enabledModules && !ctx.enabledModules.includes(String(item.visibilityKey))) return false;

  // CR-5367 rework: Admin Setup bypasses the visibility toggle for anyone
  // entitled to see it (Helix internals + customer admins). Suppliers never
  // bypass — they fall through to the menu-visibility check.
  if (item.visibilityKey === 'adminSetup' && ctx.canOpenAdminSetup) return true;

  // For a supplier sidebar, prefer supplierVisibilityKey (supplierDashboard,
  // supplierQuestionnaires, …) so the customer admin's toggle isn't shared
  // with the supplier side.
  const effectiveKey: keyof MenuVisibility = (ctx.isSupplierSidebar && item.supplierVisibilityKey)
    ? item.supplierVisibilityKey
    : item.visibilityKey;
  if (!ctx.isMenuItemVisible(effectiveKey)) return false;

  const module = getModuleForVisibilityKey(item.visibilityKey);
  // CR-8252: every compliance page also follows the company's compliance
  // switch (the Supplier Onboarding toggle), whatever its own toggle says.
  if (module === 'compliance' && !ctx.isMenuItemVisible('onboarding')) return false;
  // CR-8252: suppliers are not gated by the per-user compliance permission —
  // a supplier has compliance because it is being onboarded.
  const userGated = !(module === 'compliance' && ctx.isSupplierSidebar);
  if (module && userGated && !ctx.hasModuleAccess(module)) return false;

  return true;
}

/**
 * Flatten a navigation tree (parents + children) into one list.
 *
 * @param items - top-level nav items
 * @returns every item and child item, depth-first
 */
export function flattenNavItems(items: NavItem[]): NavItem[] {
  const out: NavItem[] = [];
  for (const item of items) {
    out.push(item);
    if (item.children && item.children.length > 0) {
      out.push(...flattenNavItems(item.children));
    }
  }
  return out;
}

/**
 * Whether a location belongs to a nav entry: the entry's own page or any
 * page beneath it (`/quality` also owns `/quality/ncr/12/edit`).
 *
 * @param pathname - the current location pathname
 * @param href - the nav item's href
 * @returns true when pathname is href or a sub-path of it
 */
export function pathMatchesHref(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(href + '/');
}

/**
 * Pages that belong to a Navigation Menu Management toggle but live outside
 * the URL prefix of the toggle's sidebar entry. Key: the page's own path
 * prefix; value: the toggle (visibilityKey) that owns it. Keyed by toggle
 * rather than by href so the same path can belong to different entries on
 * the two sides of the portal — `/rfq` is a Purchasing tab for customers but
 * the supplier sidebar's own "RFQs" entry. Without these, switching the
 * parent off would leave the page reachable by URL or bookmark — exactly the
 * gap CR-7058 closes.
 *
 * Deliberately NOT aliased: `/intelligence/qualifications` (questionnaire
 * detail) — it is opened from Supplier Onboarding reviews, Supplier detail,
 * Quality and the supplier's My Onboarding, so it is a shared surface rather
 * than a Questionnaires page.
 */
export const ROUTE_OWNER_ALIASES: Record<string, keyof MenuVisibility> = {
  // Concessions list is a Quality-module surface (opened from the Quality page).
  '/concessions': 'quality',
  // Customer RFQ is a Purchasing tab; /rfq and /rfq/:id are its pages.
  '/rfq': 'purchasing',
  // Questionnaire template builder, opened from the Questionnaires page.
  '/intelligence/templates': 'questionnaire',
  // Audit Lite: an audit is opened from Questionnaires › Audits.
  '/audits': 'questionnaire',
  // CR-8252: the onboarding wizard is a Supplier Onboarding page.
  '/suppliers/wizard': 'onboarding',
};

/**
 * The path prefixes a nav entry owns: its own href plus any aliased pages
 * whose toggle is this entry's visibilityKey.
 *
 * @param item - the nav item
 * @returns every prefix that resolves to this entry; the item's own href first
 */
export function ownedPrefixesFor(item: NavItem): string[] {
  if (!item.visibilityKey) return [item.href];
  const aliases = Object.entries(ROUTE_OWNER_ALIASES)
    .filter(([, owner]) => owner === item.visibilityKey)
    .map(([alias]) => alias);
  return [item.href, ...aliases];
}

/**
 * CR-7058: routes owned by a SUB-toggle, the ones nested under Purchasing and
 * Quality in Navigation Menu Management. These are not sidebar entries, so
 * the nav registry cannot express them, yet each sub-toggle governs a whole
 * page and not merely a tab:
 *
 *  - Approval Register and Concessions are Quality tabs that also have their
 *    own routes, reachable while the Quality module as a whole stays on.
 *  - RFQ is a Purchasing tab that also has its own routes.
 *
 * Longest matching prefix wins, and a sub-toggle always outranks the parent
 * entry because its prefix is longer or equal and it is checked first.
 */
export interface SubToggleRoute {
  /** Path prefix the sub-toggle owns. */
  prefix: string;
  /** Which settings group holds the key. */
  group: 'purchasing' | 'quality';
  /** Key within that group. */
  key: string;
  /** Name shown on the denial screen. */
  label: string;
}

export const SUBTOGGLE_ROUTES: SubToggleRoute[] = [
  { prefix: '/quality/approval-register', group: 'quality', key: 'approval_register', label: 'Approval Register' },
  { prefix: '/quality/concession', group: 'quality', key: 'concessions', label: 'Concessions' },
  { prefix: '/quality/ncr', group: 'quality', key: 'ncrs', label: 'NCRs' },
  { prefix: '/concessions', group: 'quality', key: 'concessions', label: 'Concessions' },
  { prefix: '/rfq', group: 'purchasing', key: 'rfq', label: 'RFQ' },
];

export interface SubToggleContext {
  purchasingTabs?: PurchasingTabsVisibility;
  qualityTabs?: QualityTabsVisibility;
}

/**
 * The sub-toggle that owns a location, if any, and whether it is switched on.
 * Absent keys read as enabled, matching the permissive defaults the tab
 * strips already use.
 *
 * @param pathname - the current location pathname
 * @param ctx - the loaded sub-toggle maps
 * @returns the owning sub-toggle and its state, or null when none owns the path
 */
export function resolveSubToggleForPath(
  pathname: string,
  ctx: SubToggleContext,
): { route: SubToggleRoute; enabled: boolean } | null {
  const matches = SUBTOGGLE_ROUTES
    .filter(route => pathMatchesHref(pathname, route.prefix))
    .sort((a, b) => b.prefix.length - a.prefix.length);
  const route = matches[0];
  if (!route) return null;
  const map = (route.group === 'quality' ? ctx.qualityTabs : ctx.purchasingTabs) ?? {};
  const value = (map as Record<string, boolean | undefined>)[route.key];
  return { route, enabled: value !== false };
}

/**
 * Find the navigation entry that owns a location. When several match
 * (`/suppliers` and `/suppliers/performance` both own
 * `/suppliers/performance`), the most specific matching prefix wins; at
 * equal length an entry's own href beats another entry's alias.
 *
 * @param items - the user's navigation entries (before visibility filtering)
 * @param pathname - the current location pathname
 * @returns the owning nav item, or null when no nav entry covers the path
 */
export function resolveNavItemForPath(items: NavItem[], pathname: string): NavItem | null {
  let best: NavItem | null = null;
  let bestScore = -1;
  for (const item of flattenNavItems(items)) {
    if (!item.href) continue;
    for (const prefix of ownedPrefixesFor(item)) {
      if (!pathMatchesHref(pathname, prefix)) continue;
      // Two points per character so an own-href match (+1) outranks an alias
      // of the same length without ever beating a longer prefix.
      const score = prefix.length * 2 + (prefix === item.href ? 1 : 0);
      if (score > bestScore) {
        best = item;
        bestScore = score;
      }
    }
  }
  return best;
}
